import { Table } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import { CSSProperties } from "@material-ui/styles";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import useUser from "@saleor/hooks/useUser";
import {
  maybe,
  renderCollection,
  transformOrderStatus,
  transformPaymentStatus
} from "@saleor/misc";
import { SearchRunner_search_edges_node } from "@saleor/storeOrders/types/SearchRunner";
import {
  StoreOrderListUrlSortField,
  storeOrderPath
} from "@saleor/storeOrders/urls";
import { ListProps, SortPage } from "@saleor/types";
import {
  DeliveryTypeEnum,
  PermissionEnum,
  SmopOrderStatusEnum,
  SmopUserTypeCode
} from "@saleor/types/globalTypes";
import { getPaymentChannelCode } from "@saleor/utils/paymentChannelCode/paymentChannelCode";
import { getArrowDirection } from "@saleor/utils/sort";
import classNames from "classnames";
import moment from "moment-timezone";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { StoreOrderList_storeOrders_edges_node } from "../../types/StoreOrderList";
import StoreOrderDeliveryType from "../StoreOrderDeliveryType";
import StoreOrderStatus from "../StoreOrderStatus";
import RunnerList from "./components/RunnerList";
import UpdateButtonListView from "./UpdateButtonListView";

const useStyles = makeStyles(
  theme => {
    const dataDisplay: CSSProperties = {
      flex: "60%",
      textAlign: "left"
    };

    return {
      colCustomer: {
        textAlign: "left",
        whiteSpace: "nowrap",
        width: "auto"
      },
      colDelivery: {
        whiteSpace: "nowrap",
        width: "auto"
      },
      colMall: {
        minWidth: "150px",
        width: "auto"
      },
      colNumber: {
        width: "11em",
        [theme.breakpoints.down("md")]: {
          "& thead": {
            display: "none !important"
          },
          "& thead, tbody, th, td, tr": {
            border: "none",
            display: "block"
          },
          "& tr": {
            backgroundColor: "#fff !important",
            borderBottom: "1px solid #ccc"
          },
          width: "auto"
        }
      },
      colOrderDate: {
        whiteSpace: "nowrap",
        width: "auto"
      },
      colPayment: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: "11em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colRunner: {
        width: "auto"
      },
      colStatus: {
        width: "auto"
      },
      colTargetTime: {
        whiteSpace: "nowrap",
        width: "auto"
      },
      dataTitle: {
        display: "none !important"
      },
      deliveryType: {
        marginBottom: "6px",
        marginTop: "4px"
      },
      ellipsis: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      },
      link: {
        cursor: "pointer"
      },
      statusCell: {
        display: "flex",
        flexDirection: "row"
      },
      tableCellBody: {
        [theme.breakpoints.down("lg")]: {
          fontSize: "14px"
        }
      },
      tableCellHeader: {
        [theme.breakpoints.down("lg")]: {
          fontSize: "1em"
        }
      },
      [theme.breakpoints.down("md")]: {
        dataDisplay,
        dataGroup: {
          display: "flex"
        },
        dataTitle: {
          display: "block !important",
          flex: "40%",
          fontWeight: "bold",
          textAlign: "left"
        }
      },
      tableRoot: {
        [theme.breakpoints.down("md")]: {
          "& thead": {
            display: "none !important"
          },
          "& thead, tbody, th, td, tr": {
            border: "none",
            display: "block"
          },
          "& tr": {
            backgroundColor: "#fff !important",
            borderBottom: "1px solid #ccc",
            paddingBottom: "30px",
            paddingTop: "30px"
          }
        },
        height: "60vh",
        overflowX: "auto",
        tableLayout: "auto",
        width: "100%"
      },
      tooltipHolder: {
        [theme.breakpoints.down("md")]: {
          pointerEvents: "none"
        }
      }
    };
  },
  { name: "StoreOrderList" }
);

interface RunnerFormData {
  runnerId: string;
  storeOrderId: string;
}
interface StoreOrderListProps
  extends ListProps,
    SortPage<StoreOrderListUrlSortField> {
  disableAction: boolean;
  disableMallActions: boolean;
  enableManageMallActions: boolean;
  onAssignRunner: (orderId: string) => void;
  onSubmitRunner: (data: RunnerFormData) => void;
  runnerList: SearchRunner_search_edges_node[];
  fetchRunner: (query: string) => void;
  fetchMoreRunner: any;
  onOverrideStatus: (
    orderId: string,
    orderStatus: SmopOrderStatusEnum,
    newOrderStatus: SmopOrderStatusEnum
  ) => void;
  onUpdateStatus: (
    orderId: string,
    orderStatus: SmopOrderStatusEnum,
    newOrderStatus: SmopOrderStatusEnum
  ) => void;
  orders: StoreOrderList_storeOrders_edges_node[];
  setOrderMallId: React.Dispatch<React.SetStateAction<string>>;
}

export const StoreOrderList: React.FC<StoreOrderListProps> = props => {
  const {
    disableAction,
    disableMallActions,
    enableManageMallActions,
    disabled,
    settings,
    orders,
    pageInfo,
    onPreviousPage,
    onNextPage,
    onUpdateListSettings,
    onSort,
    sort,
    onOverrideStatus,
    onUpdateStatus,
    onAssignRunner,
    onSubmitRunner,
    runnerList,
    fetchMoreRunner,
    fetchRunner,
    setOrderMallId
  } = props;
  const classes = useStyles(props);
  const {
    user,
    user: { userPermissions }
  } = useUser();
  const intl = useIntl();

  const numberOfColumns = disableMallActions ? 8 : 9;
  const isOpsAdmin =
    user?.accessGroup?.smopUsertypeCode === SmopUserTypeCode.OMI_OPS;

  const userHasMasterFulfillment = userPermissions.some(
    permission => permission.code === PermissionEnum.VIEW_MASTER_FULFILLMENT
  );

  const storeOrders = orders
    ? orders.map(order => ({
        ...order,
        paymentStatus: transformPaymentStatus(order.order.paymentStatus, intl),
        status: transformOrderStatus(order.order.status, intl)
      }))
    : undefined;

  const redirectOrder = id => {
    window.open(storeOrderPath(id));
  };

  const formatter = new Intl.NumberFormat("en-PH", {
    currency: "PHP",
    currencyDisplay: "symbol",
    minimumFractionDigits: 2,
    style: "currency"
  });

  const pathString = window.location.href;
  const mySubString = pathString
    .split("status=")
    .pop()
    .split("&")[0];

  let statusString = "";
  switch (mySubString) {
    case "NEW_ORDER":
      statusString = "New";
      break;
    case "ACKNOWLEDGED":
      statusString = "Acknowledged";
      break;
    case "READY":
      statusString = "Ready";
      break;
    case "COLLECTED":
      statusString = "Collected";
      break;
    case "RECEIVED_AT_FC":
      statusString = "Received at FC";
      break;
    default:
      break;
  }

  const getNextOrderStatus = orderStatus => {
    switch (orderStatus) {
      case SmopOrderStatusEnum.NEW_ORDER:
        return SmopOrderStatusEnum.ACKNOWLEDGED;
      case SmopOrderStatusEnum.ACKNOWLEDGED:
        return SmopOrderStatusEnum.READY;
      case SmopOrderStatusEnum.READY:
        return SmopOrderStatusEnum.COLLECTED;
      default:
        return orderStatus;
    }
  };

  return (
    <div className={classes.tableRoot}>
      <Table>
        <TableHead>
          <TableCellHeader
            direction={
              sort.sort === StoreOrderListUrlSortField.number
                ? getArrowDirection(sort.asc)
                : undefined
            }
            arrowPosition="right"
            onClick={() => onSort(StoreOrderListUrlSortField.number)}
            className={`${classes.tableCellHeader} ${classes.colNumber}`}
          >
            <FormattedMessage defaultMessage="Store Order" />
          </TableCellHeader>
          <TableCellHeader
            className={`${classes.tableCellHeader} ${classes.colStatus}`}
          >
            <FormattedMessage
              defaultMessage="Order Status"
              description="status of the order"
            />
          </TableCellHeader>
          <TableCellHeader
            direction={
              sort.sort === StoreOrderListUrlSortField.date
                ? getArrowDirection(sort.asc)
                : undefined
            }
            onClick={() => onSort(StoreOrderListUrlSortField.date)}
            className={`${classes.tableCellHeader} ${classes.colOrderDate}`}
          >
            <FormattedMessage
              defaultMessage="Order date"
              description="date when the order was placed"
            />
          </TableCellHeader>
          <TableCellHeader
            className={`${classes.tableCellHeader} ${classes.colDelivery}`}
          >
            <FormattedMessage
              defaultMessage="Delivery"
              description="delivery type and schedule"
            />
          </TableCellHeader>
          {!disableMallActions && (
            <TableCellHeader
              className={`${classes.tableCellHeader} ${classes.colMall}`}
            >
              <FormattedMessage defaultMessage="Mall" description="Mall" />
            </TableCellHeader>
          )}
          <TableCellHeader
            className={`${classes.tableCellHeader} ${classes.colPayment}`}
          >
            <FormattedMessage defaultMessage="Payment" />
          </TableCellHeader>
          <TableCellHeader
            className={`${classes.tableCellHeader} ${classes.colCustomer}`}
          >
            <FormattedMessage
              defaultMessage="Customer"
              description="customer name and email"
            />
          </TableCellHeader>
          <TableCellHeader
            className={`${classes.tableCellHeader} ${classes.colRunner}`}
          >
            <FormattedMessage
              defaultMessage="Runner Name"
              description="runner"
            />
          </TableCellHeader>
          {!userHasMasterFulfillment && (
            <>
              <TableCellHeader
                className={`${classes.tableCellHeader} ${classes.colTargetTime}`}
              >
                <FormattedMessage
                  defaultMessage="Target Time"
                  description="targetTime"
                />
              </TableCellHeader>
            </>
          )}
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={numberOfColumns}
              settings={settings}
              hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : false
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {renderCollection(
            storeOrders,
            (order, index) => (
              <TableRow
                hover={!!order}
                className={!!order ? classes.link : undefined}
                onClick={() => {
                  redirectOrder(order.id);
                }}
                key={order ? index : "skeleton"}
              >
                <TableCell
                  className={`${classes.tableCellBody} ${classes.colNumber}`}
                >
                  <div className={classes.dataGroup}>
                    <div className={classes.dataTitle}>Store Order</div>
                    <Tooltip
                      className={classes.tooltipHolder}
                      title={"#" + maybe(() => order.orderCode)}
                    >
                      <div
                        className={classNames(
                          classes.dataDisplay,
                          classes.ellipsis
                        )}
                      >
                        {maybe(() => order.order.number) ? (
                          <>
                            <span>{order.orderCode}</span>
                            <br />
                            <span>{order.tenant.brand.name}</span>
                          </>
                        ) : (
                          <Skeleton />
                        )}
                      </div>
                    </Tooltip>
                  </div>
                </TableCell>
                <TableCell
                  className={`${classes.tableCellBody} ${classes.colStatus}`}
                >
                  <div className={classes.dataGroup}>
                    <div className={classes.dataTitle}>Order Status</div>
                    <div className={classes.dataDisplay}>
                      {maybe(() => order.smopStatus) ? (
                        <div className={classes.statusCell}>
                          {" "}
                          <StoreOrderStatus status={order.smopStatus} />
                          {(isOpsAdmin || enableManageMallActions) &&
                          [
                            SmopOrderStatusEnum.NEW_ORDER,
                            SmopOrderStatusEnum.ACKNOWLEDGED,
                            SmopOrderStatusEnum.READY
                          ].includes(order.smopStatus) &&
                          [
                            DeliveryTypeEnum.EXPRESS,
                            DeliveryTypeEnum.EXPRESS_LONG_DISTANCE
                          ].includes(
                            order?.order?.shippingMethod?.deliveryType
                          ) ? (
                            <UpdateButtonListView
                              disableAction={disableAction}
                              onClickButton={() => {
                                if (
                                  [
                                    SmopOrderStatusEnum.NEW_ORDER,
                                    SmopOrderStatusEnum.ACKNOWLEDGED
                                  ].includes(order.smopStatus)
                                ) {
                                  onOverrideStatus(
                                    order?.id,
                                    order?.smopStatus,
                                    getNextOrderStatus(order?.smopStatus)
                                  );
                                } else {
                                  onUpdateStatus(
                                    order?.id,
                                    order?.smopStatus,
                                    getNextOrderStatus(order?.smopStatus)
                                  );
                                }
                              }}
                            />
                          ) : null}
                        </div>
                      ) : (
                        <Skeleton />
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell
                  className={`${classes.tableCellBody} ${classes.colOrderDate}`}
                >
                  <div className={classes.dataGroup}>
                    <div className={classes.dataTitle}>Order Date</div>
                    <div className={classes.dataDisplay}>
                      {maybe(() => order.order.created) ? (
                        <span>{moment(order.order.created).format("lll")}</span>
                      ) : (
                        <Skeleton />
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell
                  className={`${classes.tableCellBody} ${classes.colDelivery}`}
                >
                  <div className={classes.dataGroup}>
                    <div className={classes.dataTitle}>Date</div>
                    <div className={classes.dataDisplay}>
                      {maybe(() => order.order.shippingMethod.deliveryType) ? (
                        <div className={classes.deliveryType}>
                          {" "}
                          <StoreOrderDeliveryType
                            delType={
                              order?.order?.shippingMethod?.deliveryType
                                ? order.order.shippingMethod.deliveryType
                                : null
                            }
                          />
                        </div>
                      ) : (
                        <Skeleton />
                      )}

                      {maybe(() => order.order.shippingSchedule) ? (
                        <div>
                          <span>
                            {moment(order.order.shippingSchedule).format("ll")}
                          </span>
                        </div>
                      ) : (
                        <span>-</span>
                      )}
                    </div>
                  </div>
                </TableCell>
                {!disableMallActions && (
                  <TableCell
                    className={`${classes.tableCellBody} ${classes.colMall}`}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>Mall</div>
                      <div className={classes.dataDisplay}>
                        {maybe(() => order.order.mall.name) ? (
                          <span>{order.order.mall.name}</span>
                        ) : (
                          <Skeleton />
                        )}
                      </div>
                    </div>
                  </TableCell>
                )}
                <TableCell
                  className={`${classes.tableCellBody} ${classes.colPayment}`}
                >
                  <div className={classes.dataGroup}>
                    <div className={classes.dataTitle}>Payment</div>
                    <div className={classes.dataDisplay}>
                      {maybe(
                        () => order.order.payments[0].paymentMethodType
                      ) ? (
                        <>
                          <span>{getPaymentChannelCode(order.order)}</span>
                          <br />
                          <span>{formatter.format(order.totalAmount)}</span>
                        </>
                      ) : (
                        <Skeleton />
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell
                  className={`${classes.tableCellBody} ${classes.colCustomer}`}
                >
                  <div className={classes.dataGroup}>
                    <div className={classes.dataTitle}>Customer</div>
                    <div className={classes.dataDisplay}>
                      {maybe(() => order.order.user) ? (
                        <>
                          <span>
                            {order.order.user.firstName +
                              " " +
                              order.order.user.lastName}
                          </span>
                          <br />
                          <span>{order.order.user.email}</span>
                        </>
                      ) : (
                        <Skeleton />
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell
                  className={`${classes.tableCellBody} ${classes.colRunner}`}
                >
                  <div className={classes.dataGroup}>
                    <div className={classes.dataTitle}>Runner Name</div>
                    <div
                      className={classes.dataDisplay}
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    >
                      <RunnerList
                        disableAction={disableAction}
                        runnerList={runnerList}
                        fetchMoreRunner={fetchMoreRunner}
                        fetchRunner={fetchRunner}
                        order={order}
                        onAssignRunner={onAssignRunner}
                        onSubmitRunner={onSubmitRunner}
                        isOpsAdmin={isOpsAdmin}
                        enableManageMallActions={enableManageMallActions}
                        setOrderMallId={setOrderMallId}
                      />
                    </div>
                  </div>
                </TableCell>
                {!userHasMasterFulfillment && (
                  <>
                    <TableCell
                      className={`${classes.tableCellBody} ${classes.colTargetTime}`}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Target Time</div>
                        <div className={classes.dataDisplay}>
                          <span>{maybe(() => order.order.targetTime)}</span>
                        </div>
                      </div>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ),
            () => (
              <TableRow>
                <TableCell colSpan={numberOfColumns}>
                  {"No " + statusString + " orders found"}
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
    </div>
  );
};
StoreOrderList.displayName = "StoreOrderList";
export default StoreOrderList;
